import React from 'react';
import { useRouter } from 'next/router';
import classnames from 'classnames';

import { isArrayEmpty } from '@helpers/ArrayHelpers';
import { UrlParams } from 'common/url-params';
import {
  RecipeBanner, RecipeCard, RecipeIngredientsModal, RecipesEmptyState
} from 'components/recipe';
import {
  Breadcrumbs, Pagination, PerRow
} from 'components/ui';
import { APP_ROUTES } from 'constants/routes';
import { useTranslation } from 'hooks/common/useTranslation';
import { useAnalytics } from 'hooks/useAnalytics';
import { useIsOpen } from 'hooks/useIsOpen';
import RecipesSearchBox from 'templates/Recipes/RecipesSearchBox/RecipesSearchBox';

import type { PaginationOnChangeEvent } from 'components/ui';

import type {
  IBreadcrumb,
  IRecipeCard,
  IRecipesListPageUrlParams,
  IStrapiPagination,
  IStrapiResponsiveImage
} from 'types';

import styles from './RecipesListingTemplate.module.scss';

interface Props {
  className?: string,
  //
  breadcrumbs: IBreadcrumb[],
  //
  title?: string,
  banner?: IStrapiResponsiveImage,
  //
  recipes: IRecipeCard[],
  pagination: IStrapiPagination,
  //
  urlParams: IRecipesListPageUrlParams,
  //
  withSearchBox?: boolean,
  withSideMargin?: boolean
}

const RecipesListingTemplate = (props: Props) => {

  const {
    className,
    //
    breadcrumbs,
    //
    title,
    banner,
    //
    recipes,
    pagination,
    //
    urlParams,
    //
    withSearchBox,
    withSideMargin
  } = props;

  // Hooks

  const router = useRouter();
  const analytics = useAnalytics();

  // Props

  const {
    currentPage,
    totalPages,
  } = pagination || {};

  // Hooks

  const [selectedRecipeCard, setSelectedRecipeCard] = React.useState<IRecipeCard>();

  const { isOpen: isModalOpen, open: openModal, close: closeModal } = useIsOpen();

  const { t } = useTranslation();

  // Handlers

  const handlePageChange = (e: PaginationOnChangeEvent) => {
    const { selected } = e;
    const { query: { slug: segments = [] } } = router;
    const url = UrlParams.setPaginationParams(selected + 1, { segments, pageType: urlParams.pageType });
    router.push(url);
  };

  const handleRecipeEnter = (recipe: IRecipeCard) => {
    analytics.viewRecipeCard(recipe?.id);
  };

  const handleRecipeClick = (recipe: IRecipeCard) => {
    analytics.selectRecipeCard(recipe?.id);
  };

  const handleRecipeViewIngredientsClick = (recipeCard: IRecipeCard, e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    analytics.selectRecipeViewIngredients(recipeCard?.id);

    setSelectedRecipeCard(recipeCard);
    openModal();
  };

  const handleSearchClear = () => {
    router.push(APP_ROUTES.RECIPES_LIST);
  };

  const handleSearch = (query: string, initialQuery: string) => {
    const newQuery = query.trim();
    if (newQuery) {
      // Should set new search query
      if (newQuery !== initialQuery) {
        router.push(`${APP_ROUTES.RECIPES_LIST_FILTERED}/search/${encodeURIComponent(newQuery)}`);
      }
    } else if (initialQuery) {
      // Should remove query
      router.push(APP_ROUTES.RECIPES_LIST);
    }

  };

  const displayedTitle = urlParams?.searchQuery
    ? t(`SEARCH.NO_OF_RESULTS${pagination?.count !== 1 ? '_PLURAL' : ''}`, { count: pagination?.count || 0, keyword: urlParams.searchQuery })
    : title;

  const hasFiltersApplied = !!urlParams.searchQuery;
  // Render

  return (
    <>

      {/* Root */}
      <div className={classnames(styles.root, className)}>

        {/* Breadcrumbs */}
        <Breadcrumbs
          className={styles.breadcrumbs}
          items={breadcrumbs}
        />

        {/* Banner */}
        {
          banner && (
            <RecipeBanner
              className={styles.banner}
              banner={banner}
              rounded
            />
          )
        }

        {/* Content */}
        <div className={classnames(styles.content, { [styles.withSideMargin]: withSideMargin })}>

          {/* Search */}
          {
            withSearchBox && (
              <RecipesSearchBox
                className={styles.search}
                initialQuery={urlParams.searchQuery}
                onSearch={handleSearch}
                onClear={handleSearchClear}
              />
            )
          }

          {/* Title */}
          {
            displayedTitle && (
              <div
                className={classnames(styles.title, { [styles.small]: !!urlParams?.searchQuery })}
              >
                {displayedTitle}
              </div>
            )
          }

          {
            isArrayEmpty(recipes)
              ? <RecipesEmptyState withRedirectButton={hasFiltersApplied} className={styles.emptyState} />
              : (
                <>
                  {/* List */}
                  <div className={styles.list}>
                    <PerRow<IRecipeCard>
                      className={styles.perRow}
                      items={recipes}
                      itemsPerRow={1}
                      xGap={16}
                      yGap={16}
                      breakpoints={BREAKPOINTS}
                      renderItem={(recipe) => {
                        return (
                          <RecipeCard
                            recipe={recipe}
                            onEnter={() => {
                              handleRecipeEnter(recipe);
                            }}
                            onClick={() => {
                              handleRecipeClick(recipe);
                            }}
                            onViewIngredientsClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              handleRecipeViewIngredientsClick(recipe, e);
                            }}
                          />
                        );
                      }}
                    />
                  </div>

                  {/* Pagination */}
                  {
                    (recipes?.length > 0 && totalPages > 1) && (
                      <Pagination
                        activePage={currentPage}
                        pageCount={totalPages}
                        onPageChange={handlePageChange}
                      />
                    )
                  }
                </>
              )
          }
        </div>

      </div>

      {/* Modal */}
      <RecipeIngredientsModal
        isOpen={isModalOpen}
        //
        title={selectedRecipeCard?.title}
        recipeId={selectedRecipeCard?.id}
        recipeSlug={selectedRecipeCard?.slug}
        //
        onClose={() => {
          closeModal();
        }}
        onAfterClose={() => {
          setSelectedRecipeCard(undefined);
        }}
      />
    </>
  );
};

const BREAKPOINTS = {
  439: {
    itemsPerRow: 2,
    xGap: 16,
    yGap: 16,
  },
  639: {
    itemsPerRow: 2,
    xGap: 16,
    yGap: 16,
  },
  799: {
    itemsPerRow: 3,
    xGap: 16,
    yGap: 16,
  },
  1023: {
    itemsPerRow: 3,
    xGap: 24,
    yGap: 24,
  },
  1049: {
    itemsPerRow: 4,
    xGap: 24,
    yGap: 24,
  },
  1199: {
    itemsPerRow: 4,
    xGap: 24,
    yGap: 24,
  }
};

export default RecipesListingTemplate;
